import React, { BaseSyntheticEvent, FC, memo, useContext, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'
import { pick } from 'lodash-es'
import { FormProvider, useForm } from 'react-hook-form'
import { useWizardStep } from 'react-wizard-primitive'

import { SIGN_UP_CUSTOMER, LOGOUT_CUSTOMER, AppContext } from 'Const'
import { SignUpStates, useCustomer, useSignUpState } from 'Hooks/checkout'
import useEvent from 'Hooks/useEvent'
import { Customer } from 'Types'
import { sendGTMAccountCreatedEvent } from 'Utils/analytics/gtmEvents'
import { noty } from 'Utils/index'

import { StepContext } from '..'
import NextStepBtn from '../NextStepBtn'

import Code from './Code'
import Identify from './Identify'
import LoggedCustomerView from './LoggedCustomerView'
import Phone from './Phone'
import ResendButton from './ResendButton'
import VerifiedBadge from './VerifiedBadge'

export interface FormState {
  code?: string
  email: string
  firstName: string
  lastName: string
  password?: string
  phone: string
  subscribed?: boolean
}

const RESEND_COOLDOWN_SECONDS = 60
const DEFAULT_FORM_VALUES: FormState = {
  code: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  phone: '',
  subscribed: true,
}

const CustomerFieldsGroup: FC = () => {
  const { shop } = useContext(AppContext)
  const customer = useCustomer()
  const { checkouting, nextStep } = useContext(StepContext)
  const { isActive, resetToStep } = useWizardStep()
  const [state, setState] = useSignUpState(customer?.signedUp ? SignUpStates.COMPLETED : SignUpStates.PHONE)
  const [showDetails, setShowDetails] = useState(false)

  if (!showDetails && (state.isForm || state.isMultiple)) {
    setShowDetails(true)
  }

  const form = useForm<FormState>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: DEFAULT_FORM_VALUES,
  })
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0)
  const wasCodeStepRef = useRef(false)
  const codeInputRef = useRef<HTMLInputElement | null>(null)

  const { clearErrors, handleSubmit, reset, getValues, setValue } = form

  const [signUp, { loading: signing }] = useMutation(SIGN_UP_CUSTOMER, {
    onCompleted: (data) => {
      if (data.signUpCustomer.state == SignUpStates.COMPLETED) {
        const customer = data.signUpCustomer.customer
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
        sendGTMAccountCreatedEvent(customer as Customer)
      } else if (data.signUpCustomer.state == SignUpStates.CODE) {
        noty('Enter code from SMS', 'success')

        if (!wasCodeStepRef.current) {
          wasCodeStepRef.current = true
          startResendCooldown()
          window.requestAnimationFrame(() => {
            codeInputRef.current?.focus()
          })
        }
      }
      setState(data.signUpCustomer.state as SignUpStates)
    },
  })
  const [logout] = useMutation(LOGOUT_CUSTOMER)

  const handleLogout = useEvent(
    () =>
      void logout().then(() => {
        resetToStep()
        setState(SignUpStates.PHONE)
        setResendSecondsLeft(0)
        setShowDetails(false)
        reset(DEFAULT_FORM_VALUES)
        wasCodeStepRef.current = false
      }),
    [logout],
  )
  const startResendCooldown = useEvent(() => setResendSecondsLeft(RESEND_COOLDOWN_SECONDS), [])
  const stopResendCooldown = useEvent(() => setResendSecondsLeft(0), [])

  const onSubmit = useEvent((e?: BaseSyntheticEvent) => {
    const submit = handleSubmit((input: FormState) => {
      if (customer?.signedUp) {
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
      } else {
        void signUp({ variables: { input } })
      }
    })

    void submit(e)
  })

  const handleResend = useEvent(() => {
    if (resendSecondsLeft > 0) return
    startResendCooldown()
    clearErrors('code')
    setValue('code', '')
    codeInputRef.current?.focus()
    void signUp({ variables: { input: getValues() } })
  })

  const phoneSectionDescription =
    state.isPhone || state.isMultiple
      ? 'We use this number to send a verification code for checkout'
      : state.isCode
        ? 'We have sent a 6-digit authorization code to your number'
        : 'Phone number is successfully verified'

  return (
    <FormProvider {...form}>
      <div className="checkout-step / mb-5">
        <h2 className="checkout-step__header / h4">
          <span>Contact</span>

          {isActive ? (
            state.isPhone ? null : (
              <button className="checkout-button / ml-auto" type="button" onClick={handleLogout}>
                Log out
              </button>
            )
          ) : (
            <button className="checkout-button / ml-auto" type="button" onClick={resetToStep}>
              Change
            </button>
          )}
        </h2>

        <form className="checkout-contact-form" hidden={!isActive || customer?.signedUp} onSubmit={onSubmit}>
          {state.isCode ? (
            <p aria-live="polite" className="sr-only" role="status">
              Enter the 6-digit verification code to continue checkout.
            </p>
          ) : null}

          <div className="checkout-contact-flow">
            {customer?.signedUp ? null : (
              <section className="checkout-contact-flow__panel checkout-contact-flow__panel--phone">
                {shop.twoFactorAuth ? (
                  <>
                    <div className="checkout-contact-flow__title-row">
                      <h3 className="checkout-contact-flow__title">Verify Phone</h3>
                      {state.isCode || state.isPhone ? null : <VerifiedBadge />}
                    </div>
                    <p className="checkout-contact-flow__description mb-1">{phoneSectionDescription}</p>
                  </>
                ) : null}
                <Phone disabled={!state.isPhone} submitting={signing || checkouting} />
                <div aria-hidden={!state.isCode} className="checkout-contact-flow__code-reveal" hidden={!state.isCode}>
                  {state.isCode ? (
                    <Code ref={codeInputRef} canAutoSubmit={!(signing || checkouting)} onSubmit={onSubmit}>
                      <ResendButton duration={resendSecondsLeft} onResend={handleResend} onStop={stopResendCooldown} />
                    </Code>
                  ) : null}
                </div>
              </section>
            )}

            {showDetails || state.isPassword ? <Identify showDetails={showDetails} state={state} /> : null}
          </div>
        </form>
        {customer?.signedUp ? (
          <LoggedCustomerView email={customer.email} firstName={customer.firstName} lastName={customer.lastName} />
        ) : null}
      </div>

      {isActive ? <NextStepBtn loading={signing || checkouting} onSubmit={onSubmit} /> : null}
    </FormProvider>
  )
}

export default memo(CustomerFieldsGroup)
